import React, { useCallback, useEffect, useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";
import _ from "lodash";
import toast from "react-hot-toast";
import styled from "styled-components";

import IntegrationResult from "../IntegrationResult";

import { Button } from "~/components/Button";
import { RefreshIcon } from "~/components/icons/RefreshIcon";
import { hasCredentials, sanctionSearch, IntegrationIDs } from "~/endpoints/integrations";
import { TransactionWrap, Direction } from "~/hooks/use-transaction";
import useCurrentViewerVaspDID from "~/hooks/useCurrentViewerVaspDID";

type IntegrationDesc = "blockchain analytics" | "sanction screening";
type ErrorType = "originator" | "beneficiary";

export const datetime = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
});
export const formatterDate = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});
export const formatterTime = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "numeric",
});
export const formatterDateTime = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  hour12: true,
  timeZoneName: "short",
});

type TransactionPartProps = {
  transaction?: TransactionWrap | null;
  direction?: Direction | undefined;
  requiredFields?: string[];
  warnings?: string[];
  refreshButtonForSanctions?: boolean;
};

const showIntegrationError = (
  integrationName: string,
  integrationDesc: IntegrationDesc,
  errorType: ErrorType,
  errorMessage: string,
) => {
  toast.error(
    `There was a problem fetching ${integrationDesc} from ${integrationName} for the ${errorType} of this transfer: ${errorMessage}`,
    { style: { maxWidth: "100%" } },
  );
};

// STYLES

const SanctionListContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;
`;

const RefreshButton = styled(Button)`
  width: 32px;
  height: 32px;
  font-size: 9px;
  font-weight: 800;
  border: 1px solid #1f1f1f;
  border-radius: 4px;
  padding: 6px 8px;
  flex-shrink: 0;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const IntegrationResultWrapper = styled.div`
  flex-grow: 1;
`;

export default function ToolTipComponent({ transaction, direction, refreshButtonForSanctions }: TransactionPartProps) {
  const vaspDID = useCurrentViewerVaspDID();
  const [numberOfMatches, setNumberOfMatches] = useState<number>(0);
  const [verificationDate, setVerificationDate] = useState<Date>();
  const [hasRefinitivApiCredentials, setHasRefinitivApiCredentials] = useState<boolean>(false);
  const [isRefinitivLoading, setRefinitivLoading] = useState(false);
  const [refinitivError, setRefinitivError] = useState("");
  const [refinitivDashboardUrl, setRefinitivDashboardUrl] = useState<string>("");
  const [refinitivCaseId, setRefinitivCaseId] = useState<string>("");
  const [isLexisNexisLoading, setLexisNexisLoading] = useState(false);
  const [hasLexisNexisApiCredentials, setHasLexisNexisApiCredentials] = useState<boolean>(false);
  const [lexisNexisError, setLexisNexisError] = useState("");
  const [lexisNexisDashboardUrl, setLexisNexisDashboardUrl] = useState<string>("");
  const [lexisNexisCaseId, setLexisNexisCaseId] = useState<string>("");

  const [isComplyAdvantageLoading, setComplyAdvantageLoading] = useState(false);
  const [hasComplyAdvantageApiCredentials, setHasComplyAdvantageApiCredentials] = useState<boolean>(false);
  const [, setComplyAdvantageError] = useState("");
  const [complyAdvantageRiskLevel, setComplyAdvantageRiskLevel] = useState<{
    link: string;
    text: string;
    totalHits: number;
    createdAt: Date;
  }>();
  const [complyAdvantageCaseId, setComplyAdvantageCaseId] = useState<string>("");

  const REFINITIV_BASE_URL =
    "https://worldcheck.refinitiv.com/#accelus/fsp/%7B%22location%22%3A%22%3Flocale%3Den-GB%23fsp%2Fcase%2F";
  const REFINITIV_PARAMS = "%2Fview%2Fworldcheck%2F%257B%2522from%2522%253A%2522parent-case-manager%2522%257D%22%7D";
  const isOutgoing = direction === "outgoing";
  const transactionId = transaction?.id;
  const beneficiaryName = transaction?.beneficiaryName;
  const originatorName = transaction?.originatorName;
  const beneficiaryLegal = transaction?.beneficiaryLegal;
  const originatorLegal = transaction?.originatorLegal;
  const beneficiaryRef = transaction?.beneficiaryRef;
  const originatorRef = transaction?.originatorRef;

  const showIntegrationErrorComplyAdvantage = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setComplyAdvantageError((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error || errorMessage === "error code 500: no hit") {
          // don't do anything if this error has already been displayed
          return currentError;
        }
        showIntegrationError("ComplyAdvantage", "sanction screening", errorType, errorMessage);
        return error;
      }),
    [],
  );

  const showIntegrationErrorRefinitiv = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setRefinitivError((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error || errorMessage === "error code 500: no hit") {
          // don't do anything if this error has already been displayed
          return currentError;
        }
        showIntegrationError("Refinitiv", "sanction screening", errorType, errorMessage);
        return error;
      }),
    [],
  );

  const refinitvApiCredentialsCheck = useCallback(async () => {
    if (!vaspDID) {
      return;
    }
    const refinitvApiCredentials = await hasCredentials({ vaspDID, integrationId: IntegrationIDs.REFINITIV });
    setHasRefinitivApiCredentials(refinitvApiCredentials);
  }, [vaspDID]);

  useEffect(() => {
    refinitvApiCredentialsCheck();
  }, [refinitvApiCredentialsCheck]);

  const refinitivCheck = useCallback(
    async (refresh = false) => {
      if (!vaspDID || !hasRefinitivApiCredentials) {
        return;
      }

      const screeningName = isOutgoing ? beneficiaryName : originatorName;
      if (!screeningName) {
        return;
      }
      const entityType = isOutgoing
        ? beneficiaryLegal
          ? "ORGANISATION"
          : "INDIVIDUAL"
        : originatorLegal
        ? "ORGANISATION"
        : "INDIVIDUAL";

      try {
        setRefinitivLoading(true);
        const screeningCheck = await sanctionSearch({
          vaspDID,
          integrationId: IntegrationIDs.REFINITIV,
          data: {
            searchTerm: screeningName,
            entityType,
            transactionId,
            ...(refresh && { refresh }),
          },
        });

        setRefinitivLoading(false);
        setRefinitivCaseId(screeningCheck.metadata?.caseSystemId);
        setRefinitivDashboardUrl(`${REFINITIV_BASE_URL}${screeningCheck.metadata?.caseSystemId}${REFINITIV_PARAMS}`);
        setNumberOfMatches(screeningCheck.matches ? screeningCheck.matches.length : 0);
        setVerificationDate(screeningCheck.createdAt);
      } catch (err) {
        showIntegrationErrorRefinitiv(isOutgoing ? "beneficiary" : "originator", (err as Error).message);
      }
    },
    [
      vaspDID,
      hasRefinitivApiCredentials,
      isOutgoing,
      transactionId,
      beneficiaryName,
      originatorName,
      showIntegrationErrorRefinitiv,
      beneficiaryLegal,
      originatorLegal,
    ],
  );

  useEffect(() => {
    refinitivCheck();
  }, [refinitivCheck]);

  const showIntegrationErrorLexisNexis = useCallback(
    (errorType: ErrorType, errorMessage: string) =>
      setLexisNexisError((currentError) => {
        const error = `${errorType}/${errorMessage}`;
        if (currentError === error || errorMessage === "error code 500: no hit") {
          // don't do anything if this error has already been displayed
          return currentError;
        }
        showIntegrationError("LexisNexis", "sanction screening", errorType, errorMessage);
        return error;
      }),
    [],
  );

  const lexisNexisApiCredentialsCheck = useCallback(async () => {
    if (!vaspDID) {
      return;
    }
    const lexisNexisApiCredentials = await hasCredentials({ vaspDID, integrationId: IntegrationIDs.LEXISNEXIS });
    setHasLexisNexisApiCredentials(lexisNexisApiCredentials);
  }, [vaspDID]);

  useEffect(() => {
    lexisNexisApiCredentialsCheck();
  }, [lexisNexisApiCredentialsCheck]);

  const lexisNexisCheck = useCallback(
    async (refresh = false) => {
      if (!vaspDID || !hasLexisNexisApiCredentials) {
        return;
      }

      const screeningName = isOutgoing ? beneficiaryName : originatorName;
      if (!screeningName) {
        return;
      }
      const entityType = isOutgoing
        ? beneficiaryLegal
          ? "Business"
          : "Individual"
        : originatorLegal
        ? "Business"
        : "Individual";

      try {
        setLexisNexisLoading(true);
        const screeningCheck = await sanctionSearch({
          vaspDID,
          integrationId: IntegrationIDs.LEXISNEXIS,
          data: {
            searchTerm: screeningName,
            entityType,
            transactionId,
            ...(refresh && { refresh }),
          },
        });

        setLexisNexisLoading(false);
        setLexisNexisDashboardUrl(screeningCheck.metadata?.caseUrl);
        setLexisNexisCaseId(screeningCheck.metadata?.caseUrl.split("/").pop());
        setNumberOfMatches(screeningCheck.matches ? screeningCheck.matches.length : 0);
        setVerificationDate(screeningCheck.createdAt);
      } catch (err) {
        showIntegrationErrorLexisNexis(isOutgoing ? "beneficiary" : "originator", (err as Error).message);
      }
    },
    [
      vaspDID,
      hasLexisNexisApiCredentials,
      isOutgoing,
      beneficiaryName,
      originatorName,
      showIntegrationErrorLexisNexis,
      beneficiaryLegal,
      originatorLegal,
      transactionId,
    ],
  );

  useEffect(() => {
    lexisNexisCheck();
  }, [lexisNexisCheck]);

  const complyAdvantageCredentialsCheck = useCallback(async () => {
    if (!vaspDID) {
      return;
    }
    const complyAdvantageApiCredentials = await hasCredentials({
      vaspDID,
      integrationId: IntegrationIDs.COMPLYADVANTAGE,
    });
    setHasComplyAdvantageApiCredentials(complyAdvantageApiCredentials);
  }, [vaspDID]);

  useEffect(() => {
    complyAdvantageCredentialsCheck();
  }, [complyAdvantageCredentialsCheck]);

  const complyAdvantageCheck = useCallback(
    async (refresh = false) => {
      if (!vaspDID || !hasComplyAdvantageApiCredentials) {
        return;
      }

      const screeningName = isOutgoing ? beneficiaryName : originatorName;
      const customerRef = isOutgoing ? beneficiaryRef : originatorRef;

      if (!screeningName) {
        return;
      }
      try {
        setComplyAdvantageLoading(true);
        const screeningCheck = await sanctionSearch({
          vaspDID,
          integrationId: IntegrationIDs.COMPLYADVANTAGE,
          data: {
            searchTerm: screeningName,
            transactionId,
            ...(customerRef && { customerRef }),
            ...(refresh && { refresh }),
          },
        });

        const total_hits = screeningCheck.matches.length;

        const { match_status } = screeningCheck.metadata;
        let { linkUrl } = screeningCheck.metadata;

        if (!linkUrl) {
          linkUrl = "https://app.eu.complyadvantage.com/app/case-management/search/";
        }

        setComplyAdvantageLoading(false);
        setComplyAdvantageCaseId(screeningCheck.metadata.ref);
        setComplyAdvantageRiskLevel({
          link: `${linkUrl}${screeningCheck.metadata.ref}`,
          text: `${total_hits} Hit${total_hits > 1 ? "s" : ""}, ${_.startCase(_.toLower(match_status))}`,
          totalHits: total_hits,
          createdAt: screeningCheck.createdAt,
        });
      } catch (err) {
        showIntegrationErrorComplyAdvantage(isOutgoing ? "beneficiary" : "originator", (err as Error).message);
      }
    },
    [
      beneficiaryName,
      hasComplyAdvantageApiCredentials,
      isOutgoing,
      originatorName,
      showIntegrationErrorComplyAdvantage,
      vaspDID,
      transactionId,
    ],
  );

  useEffect(() => {
    complyAdvantageCheck();
  }, [complyAdvantageCheck]);

  const handleRefresh = useCallback(
    (integrationId: string) => {
      //Setting refresh option to true
      switch (integrationId) {
        case IntegrationIDs.REFINITIV:
          refinitivCheck(true);
          break;
        case IntegrationIDs.LEXISNEXIS:
          lexisNexisCheck(true);
          break;
        case IntegrationIDs.COMPLYADVANTAGE:
          complyAdvantageCheck(true);
          break;
        default:
          break;
      }
    },
    [complyAdvantageCheck, lexisNexisCheck, refinitivCheck],
  );

  if (!hasRefinitivApiCredentials && !hasComplyAdvantageApiCredentials && !hasLexisNexisApiCredentials) {
    return <> </>;
  }

  if (hasComplyAdvantageApiCredentials && !hasRefinitivApiCredentials && !hasLexisNexisApiCredentials) {
    return (
      <SanctionListContainer>
        <IntegrationResultWrapper>
          <IntegrationResult
            shortLabel="MATCHES"
            longLabel="MATCHES"
            provider="Comply Advantage"
            value={complyAdvantageRiskLevel?.totalHits.toString() || "0"}
            date={complyAdvantageRiskLevel?.createdAt || new Date()}
            isHighAlert={(complyAdvantageRiskLevel?.totalHits || 0) > 0}
            logo="/assets/complyAdvantage.png"
            caseId={complyAdvantageCaseId}
            linkUrl={complyAdvantageRiskLevel?.link}
          />
        </IntegrationResultWrapper>
        {refreshButtonForSanctions && (
          <RefreshButton
            onClick={() => handleRefresh(IntegrationIDs.COMPLYADVANTAGE)}
            disabled={isComplyAdvantageLoading}
          >
            <RefreshIcon />
          </RefreshButton>
        )}
      </SanctionListContainer>
    );
  }

  if (hasRefinitivApiCredentials && !hasComplyAdvantageApiCredentials && !hasLexisNexisApiCredentials) {
    return (
      <SanctionListContainer>
        <IntegrationResultWrapper>
          <IntegrationResult
            shortLabel="MATCHES"
            longLabel="POTENTIAL MATCHES"
            provider="REFINITIV"
            value={numberOfMatches.toString() || "0"}
            date={verificationDate || new Date()}
            isHighAlert={numberOfMatches > 0}
            logo="/assets/refinitiv.png"
            linkUrl={refinitivDashboardUrl}
            caseId={refinitivCaseId}
            error={!!refinitivError}
          />
        </IntegrationResultWrapper>
        {refreshButtonForSanctions && (
          <RefreshButton onClick={() => handleRefresh(IntegrationIDs.REFINITIV)} disabled={isRefinitivLoading}>
            <RefreshIcon />
          </RefreshButton>
        )}
      </SanctionListContainer>
    );
  }

  if (hasLexisNexisApiCredentials && !hasRefinitivApiCredentials && !hasComplyAdvantageApiCredentials) {
    return (
      <SanctionListContainer>
        <IntegrationResultWrapper>
          <IntegrationResult
            shortLabel="MATCHES"
            longLabel="POTENTIAL MATCHES"
            provider="LEXIS NEXIS"
            value={numberOfMatches.toString() || "0"}
            date={verificationDate || new Date()}
            isHighAlert={numberOfMatches > 0}
            logo="/assets/lexisnexis.png"
            caseId={lexisNexisCaseId}
            linkUrl={lexisNexisDashboardUrl}
            error={!!lexisNexisError}
          />
        </IntegrationResultWrapper>
        {refreshButtonForSanctions && (
          <RefreshButton onClick={() => handleRefresh(IntegrationIDs.LEXISNEXIS)} disabled={isLexisNexisLoading}>
            <RefreshIcon />
          </RefreshButton>
        )}
      </SanctionListContainer>
    );
  }

  return <> </>;
}
